import {
  VALID_BLAST_CAMPAIGN_STATES,
  VALID_TRIGGERED_CAMPAIGN_STATES,
  VALID_EXPERIMENT_STATES,
  INVALID_DRAFT_EXPERIMENT_STATES,
  INVALID_RUNNING_EXPERIMENT_STATES,
  INVALID_BASE_CAMPAIGN_STATES,
} from 'modules/experiments/constants';
import { CampaignState, CampaignType, Experiment } from 'modules/domains/campaign/types';
import { useExperimentsTranslations } from 'modules/experiments/hooks';

const handleValidCampaign = (
  hasExperiment: boolean,
  latestExperiment: Experiment,
  ruleViolations: Record<string, string>,
): { isValidCampaign: boolean; ruleViolation: string } => {
  if (!hasExperiment) {
    return { isValidCampaign: true, ruleViolation: '' };
  } else if (INVALID_RUNNING_EXPERIMENT_STATES.includes(latestExperiment.state)) {
    return { isValidCampaign: false, ruleViolation: ruleViolations.experimentRunning };
  } else if (INVALID_DRAFT_EXPERIMENT_STATES.includes(latestExperiment.state)) {
    return { isValidCampaign: false, ruleViolation: ruleViolations.experimentDraft };
  } else if (VALID_EXPERIMENT_STATES.includes(latestExperiment.state)) {
    return { isValidCampaign: true, ruleViolation: '' };
  } else {
    return {
      isValidCampaign: false,
      ruleViolation: '',
    };
  }
};

const handleInvalidCampaign = (
  campaignState: CampaignState,
  ruleViolations: Record<string, string>,
): { isValidCampaign: boolean; ruleViolation: string } => {
  if (INVALID_BASE_CAMPAIGN_STATES.includes(campaignState)) {
    return { isValidCampaign: false, ruleViolation: ruleViolations.campaign };
  } else {
    return { isValidCampaign: false, ruleViolation: ruleViolations.blastCampaign };
  }
};

export const isCampaignValid = ({
  campaignState,
  campaignTypeDefinition,
  experiments,
}: {
  campaignState: CampaignState;
  campaignTypeDefinition: CampaignType;
  experiments: Experiment[];
}): { isValidCampaign: boolean; ruleViolation: string } => {
  const { t } = useExperimentsTranslations();
  const ruleViolations = {
    campaign: t('chooseCampaign.invalidCampaignViolation'),
    blastCampaign: t('chooseCampaign.invalidBlastCampaignViolation'),
    experimentRunning: t('chooseCampaign.existingRunningExperimentViolation'),
    experimentDraft: t('chooseCampaign.existingDraftExperimentViolation'),
  };
  const validCampaignStates = {
    [CampaignType.ApiTriggered]: VALID_TRIGGERED_CAMPAIGN_STATES,
    [CampaignType.WorkflowTriggered]: VALID_TRIGGERED_CAMPAIGN_STATES,
    [CampaignType.Blast]: VALID_BLAST_CAMPAIGN_STATES,
    [CampaignType.Eligibility]: VALID_TRIGGERED_CAMPAIGN_STATES,
    [CampaignType.WithAI]: VALID_BLAST_CAMPAIGN_STATES,
    [CampaignType.WithExperiment]: VALID_BLAST_CAMPAIGN_STATES,
  };

  const latestExperiment = experiments?.[0];
  const hasExperiment = !!latestExperiment;
  if (validCampaignStates[campaignTypeDefinition].includes(campaignState)) {
    return handleValidCampaign(hasExperiment, latestExperiment, ruleViolations);
  } else {
    return handleInvalidCampaign(campaignState, ruleViolations);
  }
};
